/* eslint-disable @typescript-eslint/no-explicit-any */
import { SubstepType } from "./dtos";
import { getDefaultKmatCsticMap, getHighestVisitedStep, getKmats, getMinStepToSave } from "./redux/selectors";
import { ConfiguratorType } from "../ConfiguratorType";
import { sendLinearUpdate } from "./api/sendLinearConfiguratorUpdate";
import { sendLinearDataReset } from "./api/sendLinearStepReset";
import { sendLinearMiscChange, sendLinearStepChange } from "./api/sendLinearStepChange";
import { sendLinearResetConfigurator } from "./api/sendLinearResetConfigurator";
import { sendLinearAddToCart } from "./api/sendLinearAddToCart";
import { LinearSummaryData } from "./components/LinearSummaryData";
import { type RootState } from "../../redux/store";
import {
  BASE_DESIGNATION_SEPARATOR,
  CSTIC_AL_NAME,
  CSTIC_AR_NAME,
  CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME,
  CSTIC_CARRIAGE_LEFT_NAME,
  CSTIC_CARRIAGE_MIDDLE_NAME,
  CSTIC_CARRIAGE_RIGHT_NAME,
  CSTIC_DESIGN_TYPE_NAME,
  CSTIC_GUIDEWAY_AMOUNT_NAME,
  CSTIC_LENGTH_NAME,
  CSTIC_PART_AMOUNT_NAME,
  CSTIC_PART_SIZING_TYPE_NAME,
  CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME,
  CSTIC_SCHMIERANSCHLUSS_NAME,
  CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
  CSTIC_SIZE_NAME,
  CSTIC_WAGENBAUFORM_NAME,
  FAKE_CSTIC_CONFIGURATION_VERSION,
  FAKE_CSTIC_UNIT_LENGTH,
  GET_CARRIAGE_AMOUNT_CSTIC_NAME,
  GET_CARRIAGE_LOCATING_CSTIC_NAME,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  GET_RAIL_LOCATING_CSTIC_NAME,
  LINEAR_MIN_STEP_TO_BUY,
} from "./constants";
import { LinearSummaryParams } from "./components/LinearSummaryParams";
import { RESET_SELECTION, RESET_STANDARD } from "../common/advancedTranslationUtils";
import { RESET_STANDARD_AND_LENGTH } from "./advancedTranslationsTransformer";
import { type SubstepTile } from "./models";
import { getCsticNumberVal, trimCollapseWhitespace } from "./utils";
import { NumberFormatter as NF } from "../../utils/NumberFormatter";
import { sendEvent } from "../../feature/Analytics/ga/ga";
import { type ConfiguratorPageDefinition, type StepHardcodedData } from "~/Configurators/ConfiguratorPageDefinition";
import { grabExternalTranslation } from "~/Configurators/grabExternalTranslation";
import { fetchSimpleSubstepValue } from "~/Configurators/Linear/components/fetchSimpleSubstepValue";
import { getConfiguratorDefinition } from "~/Configurators/configuratorRegistry";
import { KmatType } from "~/Configurators/Linear/getKmatType";
import { isLengthValid } from "~/Configurators/Linear/redux/reducer";
import { RAIL_CONFIGURATOR_PRODUCT_CODE, UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import * as RailsCstics from "../Rail/v2/configuration/classic/RailsCstics";
import * as CarriageCstics from "../Carriage/v2/configuration/CarriageCstics";
import * as UnitCstics from "./v2/configuration/UnitCstics";

const UNIT_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    resetLabelKey: RESET_SELECTION,
    resetRequiresConfirmation: false,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const substeps = state.linearConfigurator.substeps;

      const systemLabel = trimCollapseWhitespace(
        await dispatch(grabExternalTranslation(fetchSimpleSubstepValue(substeps.SystemLayout) ?? "")),
      );

      sendEvent({
        event: "linearConfigInteraction",
        variant: "PSF",
        step: UNIT_CONFIGURATOR_STEP_DATA[0].gaName,
        push_context: "psf_step_completed",
        selection_data: {
          system: systemLabel,
        },
      } as any);
    },
    gaName: "1 - System",
  },
  {
    resetLabelKey: RESET_SELECTION,
    resetRequiresConfirmation: false,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const substeps = state.linearConfigurator.substeps;

      const designType = fetchSimpleSubstepValue(substeps.DesignType) ?? "";
      const sizeType = fetchSimpleSubstepValue(substeps.Size) ?? "";

      sendEvent({
        event: "linearConfigInteraction",
        variant: "PSF",
        step: UNIT_CONFIGURATOR_STEP_DATA[1].gaName,
        push_context: "psf_step_completed",
        selection_data: {
          series: designType,
          size: sizeType,
        },
      } as any);
    },
    gaName: "2 - Series & size",
  },
  {
    resetLabelKey: RESET_STANDARD_AND_LENGTH,
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const substeps = state.linearConfigurator.substeps;

      const system_coating = trimCollapseWhitespace(
        await dispatch(grabExternalTranslation(fetchSimpleSubstepValue(substeps.SystemCoating) ?? "")),
      );

      sendEvent({
        event: "linearConfigInteraction",
        variant: "PSF",
        step: UNIT_CONFIGURATOR_STEP_DATA[2].gaName,
        push_context: "psf_step_completed",
        selection_data: {
          system_coating,
          length: substeps?.Length.value ?? "",
          preload: fetchSimpleSubstepValue(substeps.Preload),
          accuracy: fetchSimpleSubstepValue(substeps.Accuracy),
        },
      } as any);
    },
    gaName: "3 - Length, preload & accuracy",
  },
  {
    resetLabelKey: RESET_STANDARD,
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const defaultKmatCstics = getDefaultKmatCsticMap(state);

      const systemLayout = state.linearConfigurator.substeps.SystemLayout!;
      const selectedTile = systemLayout.tiles[systemLayout.selectedTile!];
      const guidewayAmount = Number(selectedTile.cstics[CSTIC_GUIDEWAY_AMOUNT_NAME] ?? 0);

      const selectionData = {};

      for (let i = 1; i <= guidewayAmount; i++) {
        const set = {} as any;

        const railCsticName = GET_RAIL_LOCATING_CSTIC_NAME(i);
        const carriageCsticName = GET_CARRIAGE_LOCATING_CSTIC_NAME(i);

        if (defaultKmatCstics[railCsticName].value !== null) {
          set.rails = defaultKmatCstics[railCsticName].value === "O" ? "above" : "below";
        }
        if (defaultKmatCstics[carriageCsticName].value !== null) {
          set.carriages = defaultKmatCstics[carriageCsticName].value === "O" ? "above" : "below";
        }

        selectionData[`guideway_set_${i}`] = set;
      }

      sendEvent({
        event: "linearConfigInteraction",
        variant: "PSF",
        step: UNIT_CONFIGURATOR_STEP_DATA[3].gaName,
        push_context: "psf_step_completed",
        selection_data: selectionData,
      } as any);
    },
    gaName: "4 - Mounting variants",
  },
  {
    resetLabelKey: RESET_STANDARD,
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const substeps = state.linearConfigurator.substeps;

      const selectionData = {} as any;
      const data: Promise<string>[] = [];

      const lubrication = substeps.Lubrication;
      if (lubrication && lubrication.selectedTile !== null) {
        const lubricationTiles: SubstepTile[] = [...lubrication.tiles];
        lubrication.groupedTiles.forEach((group) => lubricationTiles.push(...group.tiles));
        data.push(dispatch(grabExternalTranslation(lubricationTiles[lubrication.selectedTile].name)));
      }

      data.push(dispatch(grabExternalTranslation(fetchSimpleSubstepValue(substeps.Attachment) ?? "")));

      const closure = substeps.Closure;
      if (closure && closure.selectedTile !== null && closure.isVisible) {
        const allTiles: SubstepTile[] = [];
        closure.groupedTiles.forEach((group) => allTiles.push(...group.tiles));

        const selectedTile = allTiles[closure.selectedTile];
        data.push(dispatch(grabExternalTranslation(selectedTile.name)));
      }

      const [lubrificationLabel, guidewayLabel, holesLabel] = await Promise.all(data);

      sendEvent({
        event: "linearConfigInteraction",
        variant: "PSF",
        step: UNIT_CONFIGURATOR_STEP_DATA[4].gaName,
        push_context: "psf_step_completed",
        selection_data: {
          lubrification: trimCollapseWhitespace(lubrificationLabel),
          guideway_location: trimCollapseWhitespace(guidewayLabel),
          holes_closing_techniques: trimCollapseWhitespace(holesLabel),
        },
      } as any);
    },
    gaName: "5 - System characteristics",
  },
  {
    resetLabelKey: RESET_STANDARD,
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const kmats = getKmats(state);

      const systemLayout = state.linearConfigurator.substeps.SystemLayout!;
      const selectedTile = systemLayout.tiles[systemLayout.selectedTile!];
      const guidewayAmount = Number(selectedTile.cstics[CSTIC_GUIDEWAY_AMOUNT_NAME] ?? 0);

      const selectionData = {};

      for (let i = 1; i <= guidewayAmount; i++) {
        const set = {} as any;

        const index = i - 1;

        set.length = kmats[KmatType.GUIDEWAY][index].cstics[CSTIC_LENGTH_NAME].value;
        const alValue = getCsticNumberVal(kmats[KmatType.GUIDEWAY][index].cstics[CSTIC_AL_NAME]);
        set.aL =
          alValue !== null ? NF.getDefaultFormatter().format(Math.ceil((alValue + Number.EPSILON) * 100) / 100) : "";

        const arValue = getCsticNumberVal(kmats[KmatType.GUIDEWAY][index].cstics[CSTIC_AR_NAME]);
        set.aR =
          arValue !== null ? NF.getDefaultFormatter().format(Math.ceil((arValue + Number.EPSILON) * 100) / 100) : "";

        if (kmats[KmatType.GUIDEWAY][index].cstics[CSTIC_PART_SIZING_TYPE_NAME].value !== null) {
          const label = kmats[KmatType.GUIDEWAY][index].cstics[CSTIC_PART_SIZING_TYPE_NAME].values.find(
            (v) => v.value === kmats[KmatType.GUIDEWAY][index].cstics[CSTIC_PART_SIZING_TYPE_NAME].value,
          )!.label;
          set.part_lengths = trimCollapseWhitespace(await dispatch(grabExternalTranslation(label)));
        }

        const partAmount = kmats[KmatType.GUIDEWAY][index].cstics[CSTIC_PART_AMOUNT_NAME]?.value;
        set.part_amount = partAmount ? Number(partAmount) : "-";

        for (let j = 1; j <= 5; j++) {
          const cstic = kmats[KmatType.GUIDEWAY][index].cstics[GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(j)];
          const csticNumericValue = getCsticNumberVal(cstic);
          set[`part_${j}_length`] =
            csticNumericValue !== null ? NF.getDefaultFormatter().format(csticNumericValue) : "-";
        }

        selectionData[`guideway_set_${i}`] = set;
      }

      sendEvent({
        event: "linearConfigInteraction",
        variant: "PSF",
        step: UNIT_CONFIGURATOR_STEP_DATA[5].gaName,
        push_context: "psf_step_completed",
        selection_data: selectionData,
      } as any);
    },
    gaName: "6 - Guideway set details",
  },
  {
    resetLabelKey: RESET_STANDARD,
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const kmats = getKmats(state);
      const substeps = state.linearConfigurator.substeps;

      const systemLayout = state.linearConfigurator.substeps.SystemLayout!;
      const selectedTile = systemLayout.tiles[systemLayout.selectedTile!];
      const guidewayAmount = Number(selectedTile.cstics[CSTIC_GUIDEWAY_AMOUNT_NAME] ?? 0);
      const carriageAmount = Number(selectedTile.cstics[GET_CARRIAGE_AMOUNT_CSTIC_NAME(1)] ?? 0);

      const selectionData = {} as any;

      const smdsTile =
        substeps.SMDS.isVisible && substeps.SMDS.selectedTile !== null
          ? substeps.SMDS.tiles[substeps.SMDS.selectedTile!]
          : null;

      const [SMDS] = await Promise.all([dispatch(grabExternalTranslation(smdsTile?.name ?? "-"))]);

      selectionData.SMDS = trimCollapseWhitespace(SMDS);

      selectionData["configured_carriage_data"] = {};

      for (let j = 1; j <= guidewayAmount; j++) {
        const guidewayIndex = j - 1;
        const guidewaySet = {} as any;
        selectionData["configured_carriage_data"][`guideway_set_${j}`] = guidewaySet;
        for (let i = 1; i <= carriageAmount; i++) {
          const carriageIndex = i - 1;
          const realIndexInState = carriageIndex + guidewayIndex * carriageAmount;
          const set = {} as any;

          const singleCarriageReference = kmats[KmatType.CARRIAGE][realIndexInState].cstics[CSTIC_WAGENBAUFORM_NAME];
          const carriageRefSelectedTile = singleCarriageReference.values.find(
            (singleValue) => singleValue.value === singleCarriageReference.value,
          );

          const kitLeft = kmats[KmatType.CARRIAGE][realIndexInState].cstics[CSTIC_CARRIAGE_LEFT_NAME];
          const kitMid = kmats[KmatType.CARRIAGE][realIndexInState].cstics[CSTIC_CARRIAGE_MIDDLE_NAME];
          const kitRight = kmats[KmatType.CARRIAGE][realIndexInState].cstics[CSTIC_CARRIAGE_RIGHT_NAME];

          const lubrication = kmats[KmatType.CARRIAGE][realIndexInState].cstics[CSTIC_SCHMIERANSCHLUSS_NAME];
          const lubricationSelectedTile = lubrication?.values.find(
            (singleValue) => singleValue.value === lubrication.value,
          );

          const lubricationPosition =
            kmats[KmatType.CARRIAGE][realIndexInState].cstics[CSTIC_SCHMIERANSCHLUSS_POSITION_NAME];
          const lubricationPositionSelectedTile =
            lubricationPosition !== undefined
              ? lubricationPosition.values.find((singleValue) => singleValue.value === lubricationPosition.value)
              : { label: "-", standard: false };

          const lubricationPortSelection =
            kmats[KmatType.CARRIAGE][realIndexInState].cstics[CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME];
          const lubricationPortSelectionSelectedTile =
            lubricationPortSelection !== undefined
              ? lubricationPortSelection.values.find(
                  (singleValue) => singleValue.value === lubricationPortSelection.value,
                )
              : { label: "-", standard: false };

          const lubricationDegree =
            kmats[KmatType.CARRIAGE][realIndexInState].cstics[CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME];
          const lubricationDegreeSelectedTile =
            lubricationDegree !== undefined
              ? lubricationDegree.values.find((singleValue) => singleValue.value === lubricationDegree.value)
              : { label: "-", standard: false };

          const [
            carriage_type,
            kit_left,
            kit_centre,
            kit_right,
            lubrification_connector,
            lubrification_port_alignment,
            lubrification_port_selection,
            lubrification_port_degree,
          ] = await Promise.all([
            dispatch(grabExternalTranslation(carriageRefSelectedTile?.label ?? "-")),
            dispatch(grabExternalTranslation(kitLeft?.value ?? "-")),
            dispatch(grabExternalTranslation(kitMid?.value ?? "-")),
            dispatch(grabExternalTranslation(kitRight?.value ?? "-")),
            dispatch(grabExternalTranslation(lubricationSelectedTile?.label ?? "-")),
            dispatch(grabExternalTranslation(lubricationPositionSelectedTile?.label ?? "-")),
            dispatch(grabExternalTranslation(lubricationPortSelectionSelectedTile?.label ?? "-")),
            dispatch(grabExternalTranslation(lubricationDegreeSelectedTile?.label ?? "-")),
          ]);

          guidewaySet[`carriage_${i}`] = {
            carriage_type: trimCollapseWhitespace(carriage_type),
            kit_left: trimCollapseWhitespace(kit_left),
            kit_centre: trimCollapseWhitespace(kit_centre),
            kit_right: trimCollapseWhitespace(kit_right),
            lubrification_connector: trimCollapseWhitespace(lubrification_connector),
            lubrification_port_alignment: trimCollapseWhitespace(lubrification_port_alignment),
            lubrification_port_selection: trimCollapseWhitespace(lubrification_port_selection),
            lubrification_port_degree: trimCollapseWhitespace(lubrification_port_degree),
          };
        }
      }

      sendEvent({
        event: "linearConfigInteraction",
        variant: "PSF",
        step: UNIT_CONFIGURATOR_STEP_DATA[6].gaName,
        push_context: "psf_step_completed",
        selection_data: selectionData,
      } as any);
    },
    gaName: "7 - Carriage details",
  },
  {
    // Step 8 - confirmation
    resetLabelKey: "",
    resetRequiresConfirmation: false,
    trackCompletedStepAction: async () => {},
    gaName: "8 - Summary",
  },
];

export const LC_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.System,
  productCode: UNIT_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.unit.name",
  },
  modelPreprocessor: (model) => {
    model.steps[4].substeps = [SubstepType.Lubrication, SubstepType.Attachment, SubstepType.Closure];
    model.steps[5].substeps = [SubstepType.GuidewaySet];

    model.steps[7].substeps = [SubstepType.SummaryPage];

    model.substeps[SubstepType.SummaryPage] = {
      name: "SummaryPage",
      groupedTiles: [],
      tilesPerRow: 1,
      type: SubstepType.SummaryPage,
      tiles: [],
      info: "info",
      description: "description",
      selectedTile: null,
      value: null,
      additionalValidations: {},
      additionalData: {},
      isVisible: true,
      footerInfo: [],
      extraTexts: {},
      inputCstics: {},
      helperCstics: {},
    };
    return model;
  },
  apiCalls: {
    update: sendLinearUpdate,
    reset: sendLinearResetConfigurator,
    stepChange: sendLinearStepChange,
    stepReset: sendLinearDataReset,
    miscChange: sendLinearMiscChange,
    addToCart: sendLinearAddToCart,
  },
  summary: LinearSummaryData,
  summaryParams: LinearSummaryParams,
  isAddToCartEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= LINEAR_MIN_STEP_TO_BUY;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= getMinStepToSave(state);
  },
  stepHardcodedData: UNIT_CONFIGURATOR_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    if (getConfiguratorDefinition(RAIL_CONFIGURATOR_PRODUCT_CODE).isConflictAvoidable) {
      return true;
    }
    const avoidableCstics = [FAKE_CSTIC_UNIT_LENGTH];

    return avoidableCstics.includes(csticName);
  },
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    let designType = kmat.cstics[CSTIC_DESIGN_TYPE_NAME]?.value;
    const size = kmat.cstics[CSTIC_SIZE_NAME]?.value ?? "";
    let suffix = kmat?.cstics[FAKE_CSTIC_CONFIGURATION_VERSION]?.value;

    if (!designType) {
      return BASE_DESIGNATION_SEPARATOR;
    }

    if (designType?.includes("..-")) {
      [designType, suffix] = designType.split("..-");
    }

    return [designType + size, suffix]
      .filter(Boolean)
      .filter((item) => item !== "/")
      .join("-");
  },
  gaConfiguratorName: "PSF",
  getGaStepName: (step: number) => UNIT_CONFIGURATOR_STEP_DATA[step]?.gaName,
  modelAndUpdatePreprocessor: (model) => {
    const kmat = getMainKmat({ kmats: model.kmats });
    const cstics = kmat.cstics;

    // Step 3 Length validation
    if (model.substeps[SubstepType.Length]) {
      const mainKmat = getMainKmat({ kmats: model.kmats });
      const lengthCstic = mainKmat?.cstics[FAKE_CSTIC_UNIT_LENGTH];
      const length = lengthCstic?.value ?? null;
      const valid = length && isLengthValid(model.minLength, model.maxLength, Number.parseFloat(length));
      if (length && !valid && "conflictedCstics" in model) {
        model.conflictedCstics.push({
          id: "Invalid length",
          name: "Invalid length",
          cstics: [
            {
              name: FAKE_CSTIC_UNIT_LENGTH,
              key: FAKE_CSTIC_UNIT_LENGTH,
            },
          ],
        });
      }
    }

    [UnitCstics.RailLocatingFaceSegment1, UnitCstics.RailLocatingFaceSegment2].forEach((csticName) => {
      const cstic = cstics[csticName];
      cstic?.values?.forEach((val) => {
        val.label = `configurator.unit.cstic.railLocatingFace.${val.value}.name`;
      });
    });

    [UnitCstics.CarriageLocatingFaceSegment1, UnitCstics.CarriageLocatingFaceSegment2].forEach((csticName) => {
      const cstic = cstics[csticName];
      cstic?.values?.forEach((val) => {
        val.label = `configurator.unit.cstic.carriageLocatingFace.${val.value}.name`;
      });
    });

    let designSuffix = "";
    const designValue = cstics[UnitCstics.MaterialName]?.value;
    if (designValue === "RUE") {
      designSuffix = ".RUE";
    } else if (designValue === "KUSE") {
      designSuffix = ".KUSE";
    } else if (designValue === "KUVE") {
      designSuffix = ".KUVE";
    } else if (designValue === "KUVE..-W") {
      designSuffix = ".KUVE-W";
    }

    const railCstics = model.kmats[KmatType.GUIDEWAY].map((kmat) => kmat.cstics);

    railCstics.forEach((cstics) => {
      const partSizingCstic = cstics[RailsCstics.PartSizing];
      if (partSizingCstic) {
        partSizingCstic.label = "configurator.unit.cstic.partSizing.name";
        partSizingCstic.values?.forEach((val) => {
          val.label = `configurator.unit.cstic.partSizing.${val.value}.name`;
        });
      }
      const partAmountCstic = cstics[RailsCstics.PartAmount];
      if (partAmountCstic) {
        partAmountCstic.label = "configurator.unit.cstic.partAmount.name";
        partAmountCstic.values?.forEach((val) => {
          val.label = `configurator.unit.cstic.partAmount.${val.value}.name`;
        });
      }
    });

    const carriageCstics = model.kmats[KmatType.CARRIAGE].map((kmat) => kmat.cstics);
    carriageCstics.forEach((cstics) => {
      const lubricationConnectorStatusCstic = cstics[CarriageCstics.LubricationConnectorStatus];
      if (lubricationConnectorStatusCstic) {
        lubricationConnectorStatusCstic.label = "configurator.unit.cstic.lubricationConnector.name";
        lubricationConnectorStatusCstic.values?.forEach((val) => {
          val.label = `configurator.unit.cstic.lubricationConnectorStatus.${val.value}.name`;
        });
      }
      const lubricationConnectorCstic = cstics[CarriageCstics.LubricationConnector];
      if (lubricationConnectorCstic) {
        lubricationConnectorCstic.label = "configurator.unit.cstic.lubricationConnector.name";
        lubricationConnectorCstic.values?.forEach((val) => {
          val.label = val.value;
        });
      }

      const lubricationConnectorOrientationCstic = cstics[CarriageCstics.LubricationConnectorOrientation];
      if (lubricationConnectorOrientationCstic) {
        lubricationConnectorOrientationCstic.label = "configurator.unit.cstic.lubricationConnectorOrientation.name";
        lubricationConnectorOrientationCstic.info = "configurator.unit.cstic.lubricationConnectorOrientation.info";
        lubricationConnectorOrientationCstic.values?.forEach((val) => {
          val.label = `configurator.unit.cstic.lubricationConnectorOrientation.${val.value}.name`;
        });
      }

      const angularOrientationCstic = cstics[CarriageCstics.AngularOrientation];
      if (angularOrientationCstic) {
        angularOrientationCstic.label = "configurator.unit.cstic.angularOrientation.name";
        angularOrientationCstic.info = "configurator.unit.cstic.angularOrientation.info";
        angularOrientationCstic.values?.forEach((val) => {
          const value = val.value.replace("°", "");
          val.label = `configurator.unit.cstic.angularOrientation.${value}.name`;
        });
      }

      const wagenbauformCstic = cstics[CSTIC_WAGENBAUFORM_NAME];
      if (wagenbauformCstic) {
        wagenbauformCstic.label = "configurator.unit.step.configureCarriage.section.carriageType.name";
        wagenbauformCstic.description = "configurator.unit.step.configureCarriage.section.carriageType.description";
        wagenbauformCstic.info = "configurator.unit.step.configureCarriage.section.carriageType.info";
        wagenbauformCstic.values?.forEach((val) => {
          val.label = `configurator.unit.cstic.carriageType.${val.value}.name`;
        });
      }

      const kitsLeftCstic = cstics[CarriageCstics.KitsLeft];
      if (kitsLeftCstic) {
        kitsLeftCstic.label = "configurator.unit.cstic.kit.left.name";
        kitsLeftCstic.info = "configurator.unit.cstic.kit.left.info" + designSuffix;
      }
      const kitsCenterCstic = cstics[CarriageCstics.KitsCenter];
      if (kitsCenterCstic) {
        kitsCenterCstic.label = "configurator.unit.cstic.kit.center.name";
        kitsCenterCstic.info = "configurator.unit.cstic.kit.center.info" + designSuffix;
      }

      const kitsRightCstic = cstics[CarriageCstics.KitsRight];
      if (kitsRightCstic) {
        kitsRightCstic.label = "configurator.unit.cstic.kit.right.name";
        // "left" on purpose
        kitsRightCstic.info = "configurator.unit.cstic.kit.left.info" + designSuffix;
      }
    });

    return model;
  },
} as const;
